import * as React from 'react';
import * as styles from './Settings.css';
import { LocalizationService } from '../../../services/LocalizationService';
import { inject, observer } from 'mobx-react';
import { Store } from '../stores';
import { IADateAndTimePicker } from '../../dateAndTimePicker/DateAndTimePicker';
import DateTimeService from '../../../services/DateTimeService';
import Utils from '../../treeTargeting/utils';
import { TreeTargeting } from '../../treeTargeting';
import { Environment } from '../../../services/Environment';
import { ITargetingFields } from '../../../interfaces/ITargetingFields';
import { IAToggle } from '../../toggle/Toggle';
import { IAIcon } from '../../icon/Icon';
import { TenantSettingsService } from 'intraactive-sdk-helper';
import { ITenantSettings } from '../../../interfaces/ITenantSettings';
import ApprovalDetails from './approvalDetails/ApprovalDetails';
import { sdkContainerInstance } from '../../global';
import { IAGroup } from '../../group/Group';
import { IMessageCanCreateMorePins } from '../../../interfaces/IMessageCanCreateMorePins';
import { IAMessageBar } from '../../messageBar/MessageBar';
import { Helper } from '../../../Helper';
import { TreeTargetingForGroups } from '../../treeTargetingForGroups/TreeTargetingForGroups';
import RecurrenceSettings from './recurrenceSettings/RecurrenceSettings';

export interface IProps {
  onChange: () => void;
  store?: Store;
  canCreateMorePin: IMessageCanCreateMorePins;
}

export interface IState {
  endDateChanged: boolean;
}

@inject('store')
@observer
export class Settings extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();
  private readonly dateTimeService: DateTimeService = new DateTimeService();


  constructor(props: IProps) {
    super(props);
    this.state = {
      endDateChanged: false,
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    if (!this.props.store.tenantSettings && this.props.store.environment && this.props.store.tenant && this.props.store.token) {
      const tenantSettings = localStorage.getItem("IA_tenantSettings");
      if (tenantSettings && new DateTimeService().getDatePlusTwoMinuts(new Date(JSON.parse(tenantSettings).date)) > new Date()) {
        console.log("Using cached tenant settings");
        this.props.store.tenantSettings = JSON.parse(tenantSettings).value;
      } else {
        console.log("Fetching tenant settings again");
        const tenantSettingsService = new TenantSettingsService<ITenantSettings>(Environment.getEnvironmentForSettingsAPI(this.props.store.environment), sdkContainerInstance);
        tenantSettingsService.getSettings(this.props.store.tenant, undefined, this.props.store.token).then(setting => {
          this.props.store.tenantSettings = setting;
          Helper.setLocalStorage("IA_tenantSettings", JSON.stringify({
            date: new Date(),
            value: setting
          }));
        });
      }
    }
  }

  private getSelectedValues(): any[] {
    const selectedValues = new Array<any>();
    if (!this.props.store.applicationSettings || (this.props.store.applicationSettings && this.props.store.applicationSettings?.applicationSettings?.toLowerCase() === "profile data")) {
      const targetingFields = this.props.store.targetingFields;
      if (targetingFields) {
        const keys = Object.keys(targetingFields);
        keys.forEach((key) => {
          targetingFields[key].forEach(data => {
            selectedValues.push({
              label: data.label ? data.label : data.Label,
              id: data.termGuid ? data.termGuid : data.TermGuid,
              field: key
            });
          });
        });
      }
    } else {
      if (this.props.store.iaTargetingGroupData && this.props.store.iaTargetingGroupData !== "") {
        const groupsData = this.props.store.iaTargetingGroupData.split(";");
        groupsData.forEach((item) => {
          if (item && item !== "") {
            selectedValues.push({
              id: item
            });
          }
        });
      }
    }
    return selectedValues;
  }

  private updateTargeting(targetingDataModel: ITargetingFields[]): void {
    let objJSON = '{';
    let itemsCount = targetingDataModel.length;
    targetingDataModel.forEach((t) => {
      const valuesList = [];
      t.values.forEach((v) => {
        valuesList.push({
          Label: v.label,
          TermGuid: v.id
        });
      });
      objJSON += `\"${t.field}\": ${JSON.stringify(valuesList)}`;
      if (itemsCount - 1 !== 0) {
        objJSON += ',';
      }
      itemsCount--;
    });
    objJSON += '}';
    this.props.store.targetingFields = objJSON === "{}" ? undefined : JSON.parse(objJSON);
    this.forceUpdate();
  }

  private checkTargetingConfiguration = () => {

    // Targeting Operator definition
    let isProfileData = false;
    let defaultTargetingOperator = "OR";

    // Checking if the Application is using Profile Data
    if (this.props.store.applicationSettings && this.props.store.applicationSettings?.applicationSettings?.toLowerCase() === "profile data" &&
      this.props.store.applicationSettings?.application &&
      this.props.store.applicationSettings?.application?.default?.type === "profileData") {
      defaultTargetingOperator = "OR";
      isProfileData = true;

      if (this.props.store && this.props.store.iaTargetingOperator) {
        defaultTargetingOperator = this.props.store.iaTargetingOperator;
      }

    } else { // The application is using AD Groups
      this.props.store.applicationSettings.targetingTerms = [];
      defaultTargetingOperator = "OR";

      if (!this.props.store.iaTargetingOperatorADGroups) {
        this.props.store.iaTargetingOperatorADGroups = defaultTargetingOperator;
      } else if (this.props.store.iaTargetingOperatorADGroups) {
        defaultTargetingOperator = this.props.store.iaTargetingOperatorADGroups;
      }
    }

    return {
      isProfileData: isProfileData,
      defaultTargetingOperator: defaultTargetingOperator
    };
  }

  public render(): JSX.Element {
    let endDate: Date;
    if (this.props.store.endDate != null) {
      endDate = new Date(this.props.store.endDate);
    }
    if (!this.state.endDateChanged && this.props.store.component === "Event" && this.props.store.allDayEvent && this.props.store.endDate) {
      endDate = new Date(this.props.store.endDate);
      endDate.setMinutes(endDate.getMinutes() + endDate.getTimezoneOffset());
    }
    // Checking if we need to use Targeting
    const showTargetOptions = this.props.store.instance ? Utils.checkIfUsesTargeting(this.props.store.applicationSettings, this.props.store.instance.instanceName) : false;
    const isTargetingValid = this.props.store.validateTargeting();
    // Targeting Operator definition
    let checkTargetingConfig = this.checkTargetingConfiguration();
    const defaultTargetingOperator = checkTargetingConfig.defaultTargetingOperator;
    const isProfileData = checkTargetingConfig.isProfileData && this.props.store.iaTargetingTargetContent;
    // Push settings
    let disableAppPush = (this.props.store.approvalStatus === "Approved" || !this.props.store.instance.approvalEnabled)
    if (this.props.store.isDraft || this.props.store.creatingNewMessage || (new Date(this.props.store.startDate) > new Date())) {
      disableAppPush = false;
    }
    if (isProfileData) {
      disableAppPush = true;
    }
    if (this.props.store.recurrenceInfo?.isRecurring) {
      disableAppPush = true;
    }

    const appNotificationEnabledOnInstance = this.props.store?.instance?.pushEnabled && this.props.store.component === "Messages";
    const plingNotificationEnabledOnMessageInstance = this.props.store?.instance?.settings?.enablePushFromPling && this.props.store.component === "Messages";

    let disablePlingPush = this.props.store && ((!this.props.store.isDraft && (!this.props.store.creatingNewMessage && new Date(this.props.store.startDate) <= new Date()) && ((this.props.store.approvalStatus === "Approved") || (!this.props.store.instance.approvalEnabled))) || isProfileData);
    if (this.props.store.recurrenceInfo?.isRecurring) {
      disablePlingPush = true;
    }

    const showNotficationSettings = (this.props.store.userRole === "Administrator") || this.props.store.instance?.settings?.notificationRequiredUserRole == undefined || (this.props.store.instance?.settings?.notificationRequiredUserRole === "Members" && this.props.store.userRole === "Editor");

    let appNotificationOptions = <></>;
    let plingNotificationOptions = <></>;

    if (appNotificationEnabledOnInstance) {
      appNotificationOptions =
        <div
          style={{
            clear: "both",
            float: "left",
            width: "100%"
          }}
        >
          <IAToggle
            label={this.localizationService.strings.Messages_EditPanel_SendToApp}
            checked={this.props.store.push}
            color={this.props.store.highlightColor}
            disabled={disableAppPush}
            onChange={() => {
              this.props.store.push = !!!this.props.store.push;
              this.forceUpdate();
            }}
          />
          {isProfileData &&
            <div
              style={{
                clear: "both",
                fontSize: "12px",
                color: "gray",
              }}
            >
              <IAIcon
                title="Info"
                size={12}
                color={"gray"}
                style={{
                  float: "left",
                  marginTop: 2,
                  paddingRight: 2
                }}
              />
              <div>
                {this.localizationService.strings.Messages_EditPanel_SendUsingBotTargetingWarning}
              </div>
            </div>
          }
        </div>;
    }

    if (plingNotificationEnabledOnMessageInstance) {
      plingNotificationOptions =
        <div
          style={{
            clear: "both",
            float: "left",
            marginTop: 10,
            width: "100%"
          }}
        >
          <IAToggle
            label={this.localizationService.strings.Messages_EditPanel_SendUsingBot}
            checked={this.props.store.sendUsingBot}
            color={this.props.store.highlightColor}
            disabled={disablePlingPush}
            onChange={() => {
              this.props.store.sendUsingBot = !!!this.props.store.sendUsingBot;
              this.forceUpdate();
            }}
          />
          {this.props.store.instance?.settings?.notificationRequiredToolTip && this.props.store.sendUsingBot &&
            <div className={styles.IA_notification_toggle_text}>
              {this.props.store.instance.settings.notificationRequiredToolTip}
            </div>
          }
          {isProfileData &&
            <div
              style={{
                clear: "both",
                fontSize: "12px",
                color: "gray",
              }}
            >
              <IAIcon
                title="Info"
                size={12}
                color={"gray"}
                style={{
                  float: "left",
                  marginTop: 2,
                  paddingRight: 2
                }}
              />
              <div>
                {this.localizationService.strings.Messages_EditPanel_SendUsingBotTargetingWarning}
              </div>
            </div>
          }
        </div>;
    }

    let pushOptions =
      <div style={{
        float: "left"
      }}>
        {appNotificationOptions}
        {plingNotificationOptions}
      </div>;

    // Approvers
    if ((this.props.store.approvers == undefined || this.props.store.approvers?.length == 0) && this.props.store.instance && this.props.store.instance.approvalEnabled && this.props.store.instance.approvers?.length > 0) {
      this.props.store.approvers = this.props.store.instance.approvers as any;
    }
    // let isCurrentUserApprover: boolean = this.props.store.approvers?.filter(i => !i.disabled && this.props.store.user && (i.userPrincipalName?.toLocaleLowerCase() === this.props.store.user.userPrincipalName?.toLocaleLowerCase() || (i.email && i.email?.toLocaleLowerCase() === this.props.store.user.email?.toLocaleLowerCase()))).length > 0;;
    // let showReviewButtons: boolean = this.props.store.component === "Messages" && this.props.store.instance?.approvalEnabled && this.props.store.isReviewProcess && isCurrentUserApprover;

    const selectedValues = this.getSelectedValues();
    const pinDisabled = this.props.canCreateMorePin && !this.props.canCreateMorePin.canCreateMorePins;
    const showEnableLikesToggle = this.props.store.likesEnabledOnInstance;
    const showEnableCommentsToggle = this.props.store.commentsEnabledOnInstance && !this.props.store.isNewsLink;
    return (
      <div
        className={styles.IA_settings}
      >
        {this.props.store.isNewsLink &&
          <IAGroup label={this.localizationService.strings.Composer_SettingsLinkSettings}>
            <IAToggle
              label={this.localizationService.strings.Composer_SettingsOpenLinkInNewTab}
              checked={this.props.store.openNewsLinkInNewTab}
              color={this.props.store.highlightColor}
              onChange={() => this.props.store.openNewsLinkInNewTab = !this.props.store.openNewsLinkInNewTab}
            />
          </IAGroup>
        }
        {!this.props.store.isMessagesTemplateEditor &&
          <IAGroup label={this.localizationService.strings.Composer_SettingsStartAndEndDate}>
            {this.props.store.component === "Event" &&
              <IAToggle
                style={{
                  marginBottom: 10
                }}
                label={this.localizationService.strings.Composer_AllDayEvent}
                checked={this.props.store.allDayEvent}
                color={this.props.store.highlightColor}
                onChange={() => {
                  this.props.store.allDayEvent = this.props.store.allDayEvent != undefined ? !this.props.store.allDayEvent : true
                }}
              />
            }
            <IADateAndTimePicker
              label={this.props.store.component === "Event" ? this.localizationService.strings.Event_EditPanel_StartTime : this.localizationService.strings.Messages_EditPanel_StartTime}
              selectedDate={this.props.store.startDate != null ? (this.props.store.component === "Event" && this.props.store.allDayEvent ? new Date(`${this.dateTimeService.ConvertToYYYYMMDD(this.props.store.startDate)}T12:00:00.000Z`) : new Date(this.props.store.startDate)) : undefined}
              showTimeSelector={!this.props.store.allDayEvent}
              highlightColor={this.props.store.highlightColor}
              isClearable={false}
              required={true}
              inputWidth={237}
              onChange={(startDate: Date) => {
                this.props.store.startDate = startDate;
                if (endDate != undefined && new Date(this.props.store.startDate).getTime() > new Date(endDate).getTime()) {
                  this.props.store.endDate = this.dateTimeService.getDatePlusOneHour(new Date(this.props.store.startDate));
                }
                if (this.props.store.instance && this.props.store.instance.settings && this.props.store.instance.settings.shouldByDefaultExpireAfterAmountOfDays) {
                  let defaultEndDate = new Date(this.props.store.startDate);
                  defaultEndDate.setDate(defaultEndDate.getDate() + this.props.store.instance.settings.shouldByDefaultExpireAfterAmountOfDays);
                  this.props.store.endDate = new Date(defaultEndDate.setHours(0, 0, 0, 0));
                }
              }}
              style={{
                marginTop: 0,
                width: "100%"
              }}
              borderRadius={5}
            />
            <IADateAndTimePicker
              label={this.props.store.component === "Event" ? this.localizationService.strings.Event_EditPanel_EndTime : this.localizationService.strings.Messages_EditPanel_EndTime}
              selectedDate={endDate}
              showTimeSelector={!this.props.store.allDayEvent}
              minDate={this.props.store.startDate ? new Date(this.props.store.startDate) : undefined}
              highlightColor={this.props.store.highlightColor}
              isClearable={this.props.store.component === "Messages" ? true : false}
              required={this.props.store.component === "Event"}
              inputWidth={237}
              onChange={(endDate: Date) => {
                this.setState({ endDateChanged: true }, () => {
                  this.props.store.endDate = endDate;
                  this.forceUpdate();
                });
              }}
              onBlur={() => {
                if (this.props.store.pinnedEndDate && new Date(this.props.store.pinnedEndDate) > new Date(this.props.store.endDate)) {
                  this.props.store.pinnedEndDate = endDate;
                }
              }}
              style={{
                width: "100%"
              }}
              borderRadius={5}
            />
          </IAGroup>
        }
        {(this.props.store.component === "Event" || (this.props.store.component === "Messages" && this.props.store.tenantSettings?.admin?.enableRecurringMessages)) &&
          <IAGroup label={this.localizationService.strings.Events_Recurrence}>
            <div
              style={{
                display: "flex",
                flexDirection: "column"
              }}
            >
              <IAToggle
                style={{
                  marginBottom: 10
                }}
                label={this.localizationService.strings.Events_MakeThisRecurring}
                checked={this.props.store.recurrenceInfo?.isRecurring}
                color={this.props.store.highlightColor}
                onChange={() => {
                  if (!this.props.store.recurrenceInfo) {
                    this.props.store.recurrenceInfo = {};
                  }
                  this.props.store.recurrenceInfo.isRecurring = !!!this.props.store.recurrenceInfo?.isRecurring;
                  if (!this.props.store.recurrenceInfo.recurrenceData) {
                    this.props.store.recurrenceInfo.recurrenceData = {
                      interval: 1,
                      recurrenceType: "daily",
                      repeatForever: true,
                      daysOfWeek: []
                    }
                  }
                }}
              />
              {this.props.store.recurrenceInfo?.isRecurring &&
                <RecurrenceSettings
                  onChange={() => {
                    this.props.onChange();
                  }}
                />
              }
            </div>
          </IAGroup>
        }

        {showTargetOptions &&
          <IAGroup label={this.localizationService.strings.Composer_SettingsAudience}>
            {!this.props.store.validateTargeting() &&
              <div
                className={styles.IA_settings_error_message}
                style={{
                  clear: "both",
                  float: "left",
                  marginTop: 0,
                  marginBottom: 10
                }}
              >
                {this.localizationService.strings.Composer_ValidateTargeting}
              </div>
            }
            {checkTargetingConfig.isProfileData ?
              <TreeTargeting
                showSelectAll={!checkTargetingConfig.isProfileData}
                environment={this.props.store.environment}
                application={this.props.store.component === "Messages" ? "messages" : "events"}
                applicationData={this.props.store.applicationSettings}
                label={this.props.store.component === "Messages" ? (this.props.store.instance?.settings?.targetingRequired ? this.localizationService.strings.TargetTree_WhoShouldSeeThis : this.localizationService.strings.Messages_TargetContent) : this.localizationService.strings.Event_TargetContent}
                tenant={`${this.props.store.tenant}.sharepoint.com`}
                instance={this.props.store.instance.instanceName}
                authToken={this.props.store.token}
                apiUrl={Environment.getEnvironmentForMainAPI(this.props.store.environment)}
                selectedValues={selectedValues}
                currenNodeStructure={this.props.store.targetingExistingStructure}
                required={this.props.store.instance?.settings?.targetingRequired}
                toolTip={this.props.store.instance?.settings?.targetingRequiredToolTip}
                hideOptions={this.props.store.instance?.settings?.targetingRequired}
                showTarget={(this.props.store.iaTargetingTargetContent != null || this.props.store.instance?.settings?.targetingRequired ? (this.props.store.instance?.settings?.targetingRequired ? this.props.store.instance?.settings?.targetingRequired : this.props.store.iaTargetingTargetContent) : false)}
                onShowTargetChanged={(showTarget) => {
                  this.props.store.iaTargetingTargetContent = showTarget;
                  this.props.store.iaTargetingOperator = this.props.store.iaTargetingOperator ? this.props.store.iaTargetingOperator : defaultTargetingOperator;
                  this.props.store.iaTargetingOperatorADGroups = this.props.store.iaTargetingOperatorADGroups ? this.props.store.iaTargetingOperatorADGroups : defaultTargetingOperator;
                  if (checkTargetingConfig.isProfileData && showTarget) {
                    this.props.store.sendUsingBot = false;
                  }
                  this.props.onChange();
                }}
                onChange={(targeting, applicationSettings, groups, _selectAll, targetingExistingStructure) => {
                  if (this.props.store.instance?.settings?.targetingRequired) {
                    if (targeting?.length > 0) {
                      this.props.store.iaTargetingTargetContent = true;
                    } else {
                      this.props.store.iaTargetingTargetContent = false;
                    }
                  }

                  if (targeting) {
                    this.updateTargeting(targeting);
                  }

                  if (groups) {
                    // if (groups.length <= 1) { // If is only one group selected, it should change the operator to be empty
                    //   this.props.store.messageToEdit.iaTargetingOperatorADGroups = "";
                    // }
                    this.props.store.iaTargetingGroupData = Utils.parseTargetingGroupValues(groups);
                  }

                  if (checkTargetingConfig.isProfileData) {
                    // push/pling is not available when using profile data for targeting
                    this.props.store.push = false;
                    this.props.store.sendUsingBot = false;
                  }

                  this.props.store.applicationSettings = applicationSettings;
                  this.props.store.targetingExistingStructure = targetingExistingStructure;
                  this.props.onChange();
                }
                }
                operator={defaultTargetingOperator}
                onLogicalOperatorChanged={(op: string) => {
                  checkTargetingConfig = this.checkTargetingConfiguration();
                  if (checkTargetingConfig.isProfileData) {
                    this.props.store.iaTargetingOperator = op;
                  } else {
                    this.props.store.iaTargetingOperatorADGroups = op;
                  }
                }}
                nodeComparison={"label"}
                highlightColor={this.props.store.highlightColor}
                checkBoxBorderColor={isTargetingValid ? undefined : "red"}
              />
              :
              <TreeTargetingForGroups
                environment={this.props.store.environment}
                tenant={this.props.store.tenant}
                application={this.props.store.component === "Messages" ? "messages" : "events"}
                applicationData={this.props.store.applicationSettings}
                highlightColor={this.props.store.highlightColor}
                instance={this.props.store.instance.instanceName}
                label={this.props.store.component === "Messages" ? (this.props.store.instance?.settings?.targetingRequired ? this.localizationService.strings.TargetTree_WhoShouldSeeThis : this.localizationService.strings.Messages_TargetContent) : this.localizationService.strings.Event_TargetContent}
                selectedGroupIds={selectedValues?.map((itemData) => itemData.id)}
                required={this.props.store.instance?.settings?.targetingRequired}
                toolTip={this.props.store.instance?.settings?.targetingRequiredToolTip}
                hideOptions={this.props.store.instance?.settings?.targetingRequired}
                showTarget={(this.props.store.iaTargetingTargetContent != null || this.props.store.instance?.settings?.targetingRequired ? (this.props.store.instance?.settings?.targetingRequired ? this.props.store.instance?.settings?.targetingRequired : this.props.store.iaTargetingTargetContent) : false)}
                onShowTargetChanged={(showTarget) => {
                  this.props.store.iaTargetingTargetContent = showTarget;
                  this.props.store.iaTargetingOperator = "OR";
                  this.props.store.iaTargetingOperatorADGroups = this.props.store.iaTargetingOperatorADGroups ? this.props.store.iaTargetingOperatorADGroups : defaultTargetingOperator;
                  if (checkTargetingConfig.isProfileData && showTarget) {
                    this.props.store.sendUsingBot = false;
                  }
                  this.props.onChange();
                }}
                onGroupIdsUpdated={(ids: number[]) => {
                  let values = "";
                  ids?.forEach((id: number) => {
                    values += ";" + id + ";";
                  });
                  this.props.store.iaTargetingGroupData = values;
                  this.props.onChange();
                  console.log(ids);
                }}
              />
            }
          </IAGroup>
        }
        {(this.props.store.component === "Messages" && this.props.store.instance?.settings?.enablePinMessages && this.props.store.userRole === "Administrator") &&
          <IAGroup label={this.localizationService.strings.Composer_SettingsPin}>
            {pinDisabled &&
              <IAMessageBar
                type={"error"}
                style={{
                  clear: "both",
                  float: "left",
                  borderRadius: 5,
                  padding: "0 20px",
                  marginBottom: 15,
                  width: "calc(100% - 40px)"
                }}
                icon="Attention"
                showIcon
                justifyContent="left"
                content={
                  <div>
                    {this.localizationService.strings.Composer_SettingsMaxPinned?.replace("{MAX_AMOUNT}", `${this.props.canCreateMorePin?.maxPinnedMessages}`)}
                  </div>
                }
                onDismiss={() => { }}
              />
            }
            <IAToggle
              label={this.localizationService.strings.Composer_SettingsPinMessageToTop}
              disabled={pinDisabled}
              checked={this.props.store.isPinned}
              color={this.props.store.highlightColor}
              onChange={() => {
                this.props.store.isPinned = !this.props.store.isPinned;
                if (!this.props.store.isPinned) {
                  this.props.store.pinnedEndDate = undefined;
                  this.props.store.validatePinDate();
                  this.props.onChange();
                } else {
                  // default to 7 days in the future
                  this.props.store.pinnedEndDate = new Date(new Date().setDate(new Date().getDate() + 7));
                }
              }}
            />
            {this.props.store.isPinned && !this.props.store.isMessagesTemplateEditor &&
              <>
                <IADateAndTimePicker
                  label={this.localizationService.strings.Composer_SettingsPinUntil}
                  disabled={pinDisabled}
                  selectedDate={this.props.store.pinnedEndDate ? new Date(this.props.store.pinnedEndDate) : undefined}
                  showTimeSelector
                  minDate={this.props.store.startDate ? new Date(this.props.store.startDate) : undefined}
                  maxDate={this.props.store.endDate ? new Date(this.props.store.endDate) : undefined}
                  highlightColor={this.props.store.highlightColor}
                  isClearable={false}
                  required={true}
                  inputWidth={237}
                  onChange={(endDate: Date) => {
                    this.props.store.pinnedEndDate = endDate;
                    this.props.store.validatePinDate();
                    this.props.onChange();
                  }}
                  style={{
                    width: "100%"
                  }}
                  borderRadius={5}
                />
                {!this.props.store.validatePinDate() &&
                  <div
                    className={styles.IA_settings_error_message}
                    style={{
                      clear: "both",
                      float: "left"
                    }}
                  >
                    {this.localizationService.strings.Composer_ValidatePin}
                  </div>
                }
              </>
            }
          </IAGroup>
        }
        {(appNotificationEnabledOnInstance || plingNotificationEnabledOnMessageInstance) && showNotficationSettings &&
          <IAGroup label={this.localizationService.strings.Composer_SettingsNotifications}>
            {pushOptions}
          </IAGroup>
        }
        {(showEnableLikesToggle || showEnableCommentsToggle) &&
          <IAGroup label={this.localizationService.strings.Composer_SettingsSocial}>
            {showEnableLikesToggle &&
              <IAToggle
                label={this.localizationService.strings.Composer_AllowLikes}
                checked={this.props.store.recurrenceInfo?.isRecurring ? false : this.props.store.likesEnabled}
                color={this.props.store.highlightColor}
                disabled={this.props.store.recurrenceInfo?.isRecurring}
                onChange={() => {
                  this.props.store.likesEnabled = !this.props.store.likesEnabled;
                }}
              />
            }
            {showEnableCommentsToggle &&
              <IAToggle
                label={this.localizationService.strings.Composer_AllowComments}
                checked={this.props.store.recurrenceInfo?.isRecurring ? false : this.props.store.commentsEnabled}
                color={this.props.store.highlightColor}
                disabled={this.props.store.recurrenceInfo?.isRecurring}
                onChange={() => {
                  this.props.store.commentsEnabled = !this.props.store.commentsEnabled;
                  this.forceUpdate();
                }}
              />
            }
          </IAGroup>
        }

        {this.props.store.instance && this.props.store.instance.approvalEnabled && this.props.store.component === "Messages" &&
          <IAGroup label={this.localizationService.strings.Composer_SettingsApprovers}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%"
              }}
            >
              <ApprovalDetails
                onChange={() => this.props.onChange()}
              />
              {!this.props.store.validateApprovers() &&
                <div className={styles.IA_settings_error_message}>{this.localizationService.strings.Composer_ValidateApprovers}</div>
              }
            </div>
          </IAGroup>
        }
      </div>
    );
  }


}
